'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import BaseAuctionCard from './BaseAuctionCard';
import { calculateTimeRemaining } from '@/lib/utils';

interface ActiveBidCardProps {
  id: string;
  slug: string;
  title: string;
  location: string;
  currentBid: number;
  myBid: number;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
  };
  image: string;
  isHighestBidder: boolean;
  endDate?: string; // Müzayede bitiş tarihi
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isAuthenticated?: boolean; // Server-side'dan gelecek
}

export default function ActiveBidCard({
  id,
  slug,
  title,
  location,
  currentBid,
  myBid,
  timeLeft,
  image,
  isHighestBidder,
  endDate,
  isFavorite = false,
  onFavoriteToggle,
  isAuthenticated = false
}: ActiveBidCardProps) {
  const t = useTranslations('profile');
  const params = useParams();
  const [timeRemaining, setTimeRemaining] = useState(timeLeft);

  useEffect(() => {
    if (!endDate) {
      // Eğer endDate yoksa eski manuel countdown kullan
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          let { days, hours, minutes } = prev;
          
          if (minutes > 0) {
            minutes--;
          } else if (hours > 0) {
            hours--;
            minutes = 59;
          } else if (days > 0) {
            days--;
            hours = 23;
            minutes = 59;
          }
          
          return { days, hours, minutes };
        });
      }, 60000);

      return () => clearInterval(timer);
    } else {
      // endDate varsa calculateTimeRemaining kullan
      const timer = setInterval(() => {
        const calculated = calculateTimeRemaining(endDate);
        setTimeRemaining({
          days: calculated.days,
          hours: calculated.hours,
          minutes: calculated.minutes
        });
        
        // Müzayede süresi dolduysa timer'ı durdur
        if (calculated.isExpired) {
          clearInterval(timer);
          // Timer durduruldu, sayfa yenileme yok
        }
      }, 60000);

      return () => clearInterval(timer);
    }
  }, [endDate]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };


  const favoriteButton = isAuthenticated && onFavoriteToggle ? (
    <button
      onClick={() => onFavoriteToggle(id)}
      className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
    >
      <svg 
        className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
        fill={isFavorite ? 'currentColor' : 'none'} 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    </button>
  ) : null;

  const imageOverlay = (
    <>
      {/* Live Indicator */}
      <div className="absolute top-4 left-4 w-3 h-3 bg-green-500 rounded-full shadow-lg shadow-green-500/50 animate-[blink_3s_ease-in-out_infinite]"></div>
      
      {/* Time Overlay */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
        <div className="flex items-center justify-center gap-4 text-white">
          <div className="text-center">
            <div className="text-2xl font-bold leading-none">{timeRemaining.days}</div>
            <div className="text-xs uppercase">{t('bids.timeUnits.days')}</div>
          </div>
          <div className="text-2xl font-bold">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold leading-none">{timeRemaining.hours.toString().padStart(2, '0')}</div>
            <div className="text-xs uppercase">{t('bids.timeUnits.hours')}</div>
          </div>
          <div className="text-2xl font-bold">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold leading-none">{timeRemaining.minutes.toString().padStart(2, '0')}</div>
            <div className="text-xs uppercase">{t('bids.timeUnits.minutes')}</div>
          </div>
        </div>
      </div>
    </>
  );


  const content = (
    <>
      <div className="space-y-3 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600 text-sm">{t('bids.active.currentBid')}</span>
          <span className="text-2xl font-bold text-gray-900">
            {formatPrice(currentBid)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 text-sm">{t('bids.active.myBid')}</span>
          <span className={`text-2xl font-bold ${isHighestBidder ? 'text-green-600' : 'text-gray-900'}`}>
            {formatPrice(myBid)}
          </span>
        </div>
      </div>

      <div className={`mb-4 p-2 text-xs rounded text-center font-semibold ${
        isHighestBidder 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {isHighestBidder ? t('bids.active.winning') : t('bids.active.losing')}
      </div>
    </>
  );

  const actionButton = (
    <div className="mt-auto">
      <Link href={`/${params.locale}/muzayedeler/${slug}`}>
        <button className="w-full py-3 px-4 rounded-lg font-bold text-white bg-red-600 hover:bg-red-700 transition-colors">
          {t('bids.active.bidNow')}
        </button>
      </Link>
    </div>
  );

  return (
    <BaseAuctionCard
      id={id}
      title={title}
      location={location}
      image={image}
      imageOverlay={imageOverlay}
      favoriteButton={favoriteButton}
      actionButton={actionButton}
    >
      {content}
    </BaseAuctionCard>
  );
}
